import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Link2, Check } from "lucide-react";
import { api, formatDate } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { ArticleRenderer } from "../components/ArticleRenderer";
import { MaskedLine, Reveal } from "../components/Reveal";

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [article, setArticle] = useState(undefined);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setArticle(undefined);
    api.get(`/articles/${slug}`, { params: { lang } }).then((r) => setArticle(r.data)).catch(() => setArticle(null));
  }, [slug, lang]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Luísa Azevedo Lopes`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta && article.excerpt) meta.setAttribute("content", article.excerpt);
    }
  }, [article]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // ignorar
    }
  };

  if (article === undefined)
    return (
      <main className="pt-40 pb-32 min-h-screen text-center" data-testid="article-loading">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#9AA096]">{t("article.loading")}</span>
      </main>
    );

  if (article === null)
    return (
      <main className="pt-40 pb-32 min-h-screen text-center" data-testid="article-not-found">
        <p className="font-serif italic text-3xl text-forest mb-6">{t("article.notFound")}</p>
        <Link to="/blog" data-testid="article-back-link" className="btn-outline">
          <ArrowLeft size={14} /> {t("article.back")}
        </Link>
      </main>
    );

  return (
    <main data-testid="article-page" className="pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Reveal y={16}>
          <Link
            to="/blog"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#757A70] hover:text-forest transition-colors mb-12"
          >
            <ArrowLeft size={13} /> {t("article.back")}
          </Link>
        </Reveal>

        <header className="mb-12">
          <p className="overline-label mb-6" data-testid="article-meta">
            {t(`cat.${article.category}`)} · {formatDate(article.created_at, lang)} · {article.read_time} {t("article.minRead")}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight leading-[1.15] text-ebony" data-testid="article-title">
            <MaskedLine delay={0.1}>{article.title}</MaskedLine>
          </h1>
          {article.excerpt && (
            <Reveal delay={0.2}>
              <p className="text-lg text-[#4D5248] leading-relaxed mt-8 font-serif italic">
                {article.excerpt}
              </p>
            </Reveal>
          )}
          <Reveal delay={0.25}>
            <div className="flex items-center justify-between border-y border-[#E2E4DC] py-4 mt-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#757A70]">
                {t("article.by")} {article.author}
              </p>
              <button
                onClick={copyLink}
                data-testid="article-copy-link-button"
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-forest hover:text-gold transition-colors"
              >
                {copied ? <Check size={13} /> : <Link2 size={13} />}
                {copied ? t("article.copied") : t("article.copy")}
              </button>
            </div>
          </Reveal>
        </header>

        {article.cover_image && (
          <Reveal delay={0.3}>
            <div className="aspect-[16/9] overflow-hidden bg-sage mb-14" data-testid="article-cover">
              <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <ArticleRenderer content={article.content} />
        </Reveal>
      </div>
    </main>
  );
}
